import React, { useState, useCallback } from "react";
import { IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { notifyError } from "../ToastContainer/Toast/Toast";

export interface CopyToClipboardIconProps {
  /** Text to copy to clipboard */
  value: string;
  /** Tooltip when idle (default: "Copy to clipboard") */
  title?: string;
  /** Icon size in px (default: 18) */
  size?: number;
  /** Disable the button */
  disabled?: boolean;
}

/**
 * Reusable icon button that copies the given value to the clipboard on click
 * and shows brief "Copied!" tooltip feedback.
 */
export default function CopyToClipboardIcon({
  value,
  title = "Copy to clipboard",
  size = 18,
  disabled = false,
}: CopyToClipboardIconProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!value || disabled) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      notifyError("Failed to copy");
    }
  }, [value, disabled]);

  return (
    <Tooltip title={copied ? "Copied!" : title} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={handleCopy}
          disabled={disabled || !value}
          sx={{
            p: 0.25,
            border: "none",
            outline: "none",
            boxShadow: "none",
            "&:focus, &:focus-visible": { border: "none", outline: "none", boxShadow: "none" },
            "&:hover": { border: "none", backgroundColor: "action.hover" },
          }}
          aria-label={title}
        >
          <ContentCopyIcon sx={{ fontSize: size }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}
